import type { FONT_FAMILIES } from '@dg-embalajes/catalog';

export const APP_VERSION = 'v5.07';
export const APP_BUILD_ID = import.meta.env.VITE_DG_BUILD_ID as string;
export const APP_BUILD_ENVIRONMENT = import.meta.env.VITE_DG_BUILD_ENVIRONMENT as string;

export const COPY = {
  title: 'Diseñá el ploteo de tu envase',
  description:
    'Configurador para tubos de cartón y stickers. Prepará tu diseño, descargá el PDF a medida real y envialo por WhatsApp para revisión.',
  chooseProduct: '¿Qué querés preparar?',
  chooseProductHelp: 'Usá ploteo para el cuerpo del tubo o stickers para tapas y etiquetas a medida.',
  tubeMode: 'Ploteo de tubos',
  stickerMode: 'Stickers',
  chooseDiameter: '1. Elegí el diámetro del tubo',
  diameterHelp:
    'Seleccioná el diámetro que compraste o que te indicamos por WhatsApp. Esta herramienta es para tubos de cartón con tapas de hojalata.',
  enterHeight: '2. Ingresá el alto del tubo',
  heightPlaceholder: 'Ej: 15 o 27,5',
  heightHelp: 'Permitido: de 3 cm a 60 cm. Podés usar coma o punto para decimales.',
  createWorkArea: 'Crear mesa de trabajo',
  noDesignKnowledge:
    'No hace falta saber diseño: elegí un fondo, subí tu logo, agregá texto y descargá el PDF final. Si no sabés qué medida compraste, revisá la tienda o escribinos por WhatsApp.',
  chooseBackground: '3. Elegí el fondo',
  backgroundHelp: 'Abrí una carpeta de patrones y elegí una textura del catálogo premium DG embalajes.',
  uploadBackground: 'O subí una imagen de fondo propia',
  uploadLogo: '4. Subí tu logo o imagen',
  imageFormats:
    'Podés subir una foto del celular, un logo, una imagen o un PDF. La app prepara el archivo automáticamente.',
  workArea: 'Mesa de trabajo',
  textAndSettings: '5. Texto y ajustes',
  textPlaceholder: 'Ej: Marca, sabor, frase...',
  downloadDesign: '6. Descargar diseño',
  downloadSticker: '5. Descargar y enviar',
  realPdfNote: 'El PDF sale a medida real. Las cotas y la vista de pantalla no se imprimen.',
  printWarning:
    'Importante: para imprimir necesitamos el archivo PDF final. No envíes capturas de pantalla: no sirven para imprimir con buena calidad.',
  whatsappOpen: 'Abrir WhatsApp de DG',
  store: 'Ver precios y medidas',
  pwaTitle: 'Instalá el diseñador en tu celular',
  pwaDescription: 'Agregalo a tu pantalla de inicio para usarlo como una app, sin buscar el link cada vez.',
  pwaAction: 'Instalar diseñador',
  updateTitle: 'Nueva actualización disponible',
  updateDescription: 'Hay una versión nueva del diseñador. Actualizá para usar la última mejora.',
  updateNow: 'Actualizar ahora',
  updateLater: 'Después',
} as const;

type FontFamily = (typeof FONT_FAMILIES)[number]['family'];

export const FONT_LONG_LABELS: Record<FontFamily, string> = {
  Montserrat: 'Moderna limpia',
  Poppins: 'Moderna redondeada',
  Raleway: 'Moderna elegante',
  Rubik: 'Moderna suave',
  Ubuntu: 'Tecnológica simple',
  'Playfair Display': 'Elegante premium',
  'Cormorant Garamond': 'Lujo clásico',
  Lora: 'Editorial cálida',
  Merriweather: 'Editorial fuerte',
  'Libre Baskerville': 'Clásica editorial',
  Cinzel: 'Premium mayúscula',
  'Roboto Slab': 'Slab artesanal',
  Oswald: 'Comercial fuerte',
  'Bebas Neue': 'Impacto moderno',
  Anton: 'Extra fuerte',
  Caveat: 'Artesanal manuscrita',
  'Dancing Script': 'Manuscrita elegante',
  Pacifico: 'Script marca',
  'Great Vibes': 'Firma elegante',
  Lobster: 'Retro llamativa',
};

export const TEXT_COLORS = [
  { name: 'Negro carbón', value: '#17191d' },
  { name: 'Blanco', value: '#ffffff' },
  { name: 'Crema', value: '#fff4d8' },
  { name: 'Beige premium', value: '#d8c5a5' },
  { name: 'Dorado viejo', value: '#9a7330' },
  { name: 'Dorado brillante', value: '#dcae32' },
  { name: 'Cobre', value: '#a85d32' },
  { name: 'Marrón cuero', value: '#5c3424' },
  { name: 'Bordó', value: '#741f35' },
  { name: 'Rojo tierra', value: '#a3382b' },
  { name: 'Verde botella', value: '#164c38' },
  { name: 'Verde oliva', value: '#68733e' },
  { name: 'Azul marino', value: '#17365d' },
  { name: 'Azul petróleo', value: '#14606b' },
  { name: 'Rosa viejo', value: '#b46c78' },
  { name: 'Violeta suave', value: '#85629a' },
  { name: 'Gris medio', value: '#737b86' },
] as const;

export const STROKE_COLORS = [
  { name: 'Sin contorno', value: null },
  { name: 'Negro', value: '#111111' },
  { name: 'Blanco', value: '#ffffff' },
  { name: 'Dorado', value: '#c39a34' },
] as const;

export const STROKE_WIDTHS = [
  { name: 'Fino', value: 0.35 },
  { name: 'Medio', value: 0.7 },
  { name: 'Grueso', value: 1.2 },
] as const;

export function patternAssetUrl(assetPath: string, variant: 'thumb' | 'full'): string {
  const relative = assetPath.replace(/^patrones\//, '');
  return `/assets/patterns/${variant === 'thumb' ? 'thumbs' : 'full'}/${relative}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MiB`;
}

export function honestWhatsAppMessage(product: 'tube' | 'sticker'): string {
  return product === 'tube'
    ? 'Hola, ya terminé mi diseño de envase. Voy a adjuntar el PDF para revisión y producción.'
    : 'Hola, armé un PDF de sticker a medida real. Voy a adjuntarlo para revisión.';
}
